import type {
  LandingContactChannel,
  LandingCourseCapability,
  LandingCourseDefinition,
  LandingCourseDetail,
  LandingCourseUnit,
  LandingFaqItem,
  LandingFeature,
  LandingHeroPill,
  LandingHeroStat,
  LandingCommitment,
  LandingLiveCapability,
  LandingLiveDefinition,
  LandingLiveMock,
  LandingNavLink,
  LandingPlan,
  LandingPricingFeature,
  LandingSimulator,
  LandingSimulatorCapability,
  LandingSimulatorDefinition,
  LandingSimulatorMock,
  LandingSimulatorTab,
  LandingSocialLink,
  LandingStep,
  LandingTestimonial,
  LandingValue
} from '../types/landing.types'

/**
 * Copy de la landing page.
 *
 * Contenido estático: la API de publicaciones (`GET /api/posts/published`)
 * responde 200 pero devuelve `data: []`, por lo que la sección de noticias
 * no puede depender de ella.
 */

export const BRAND = {
  name: 'AppMisionate',
  tagline: 'Plataforma Digital de Cursos',
  email: 'hola@appmisionate.com',
  supportEmail: 'soporte@appmisionate.com',
  city: 'Mexicali, Baja California, México',
  hours: 'Lun – Vie 9:00 – 18:00 hrs (CST)',
  facebook: 'https://www.facebook.com/appmision.mx/',
  instagram: 'https://www.instagram.com/appmision/',
  copyright: '© 2026 AppMisionate. Todos los derechos reservados.'
} as const

export const navLinks: LandingNavLink[] = [
  { href: '#simuladores', label: 'Simuladores' },
  { href: '#cursos', label: 'Cursos' },
  { href: '#clases-en-vivo', label: 'Clases en vivo' },
  { href: '#nosotros', label: 'Nosotros' },
  { href: '#noticias', label: 'Blog' },
  { href: '#contacto', label: 'Contacto' }
]

export const heroPills: LandingHeroPill[] = [
  {
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Temario oficial vigente'
  },
  {
    icon: 'M22 11.08V12a10 10 0 11-5.93-9.14|M22 4L12 14.01l-3-3',
    label: 'Calificación inmediata'
  },
  {
    icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    label: 'Plataforma 100% web'
  },
  {
    icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    label: 'Acceso inmediato'
  }
]

export const heroStats: LandingHeroStat[] = [
  {
    icon: 'M22 10v6M2 10l10-5 10 5-10 5z|M6 12v5c3 3 9 3 12 0v-5',
    iconClass: 'wl-hsi-blue',
    stroke: '#64B5F6',
    title: 'Preparación de Alto Nivel',
    description: 'Temarios oficiales actualizados'
  },
  {
    icon: 'M12 12m-10 0a10 10 0 1020 0a10 10 0 10-20 0|M12 12m-6 0a6 6 0 1012 0a6 6 0 10-12 0|M12 12m-2 0a2 2 0 104 0a2 2 0 10-4 0',
    iconClass: 'wl-hsi-red',
    stroke: '#E57373',
    title: 'Estrategias Comprobadas',
    description: 'Métodos usados por estudiantes aprobados'
  },
  {
    icon: 'M3 4h18v16H3z|M3 10h18M8 4v3M16 4v3',
    iconClass: 'wl-hsi-blue-light',
    stroke: '#90CAF9',
    title: 'Simulacros Realistas',
    description: 'Con contenido real como en los exámenes oficiales, con tiempo real'
  },
  {
    icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2|M9 11a4 4 0 100-8 4 4 0 000 8|M23 21v-2a4 4 0 00-3-3.87|M16 3.13a4 4 0 010 7.75',
    iconClass: 'wl-hsi-blue-lighter',
    stroke: '#BBDEFB',
    title: 'Acompañamiento Personalizado',
    description: 'Soporte y seguimiento de tu progreso'
  }
]

/**
 * Bloque "qué es un simulador".
 *
 * Las tres tarjetas resuelven el problema de entrada del visitante: qué es un
 * simulador y en qué se parece (y en qué no) al examen real.
 */
export const simulatorDefinitions: LandingSimulatorDefinition[] = [
  {
    icon:
      'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2|M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z|m9 14 2 2 4-4',
    title: 'Mismo temario, mismo formato',
    description:
      'Las preguntas y las secciones están armadas con la estructura del examen oficial, no con reactivos genéricos.'
  },
  {
    icon: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z',
    title: 'Corrección al instante',
    description:
      'Terminas y ves tu puntaje, tu porcentaje y el detalle de cada reactivo: qué acertaste y por qué.'
  },
  {
    icon: 'M3 12a9 9 0 1 0 3-6.7L3 8|M3 3v5h5',
    title: 'Practica sin límite',
    description:
      'Repites las veces que quieras: al reiniciar el intento se borra tu avance, nunca tu aprendizaje.'
  }
]

/**
 * Bloque "qué puedes hacer con los simuladores".
 *
 * Cada capacidad corresponde a una función que el producto ya soporta
 * (secciones, simulador temporal, temporizador, corrección por reactivo,
 * avance persistido, resultado por sección y repetición del intento).
 */
export const simulatorCapabilities: LandingSimulatorCapability[] = [
  {
    icon: 'm12 2 9 5-9 5-9-5 9-5z|m3 12 9 5 9-5|m3 17 9 5 9-5',
    title: 'Practica por secciones',
    description: 'Entra directo a la parte del temario que te cuesta, sin responder todo el examen.'
  },
  {
    icon: 'M16 3h5v5|M4 20 21 3|M21 16v5h-5|m15 15 6 6|M4 4l5 5',
    title: 'Simulador libre',
    description: 'Un examen al azar por dificultad y tema, distinto en cada intento.'
  },
  {
    icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M12 6v6l4 2',
    title: 'Con temporizador',
    description: 'Repite la presión del tiempo real, con reloj global o por sección.'
  },
  {
    icon: 'm3 7 2 2 4-4|M11 7h10|m3 17 2 2 4-4|M11 17h10',
    title: 'Corrección reactivo a reactivo',
    description: 'Ves tu respuesta contra la correcta, con la explicación de cada una.'
  },
  {
    icon: 'M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z',
    title: 'Tu avance se guarda',
    description: 'Cierras la página y sigues donde ibas otro día.'
  },
  {
    icon: 'M3 3v18h18|M7 16v-4|M12 16V8|M17 16v-6',
    title: 'Resultado por sección',
    description: 'Aciertos por área y en qué lugar quedas frente a otros.'
  },
  {
    icon: 'M3 12a9 9 0 1 0 3-6.7L3 8|M3 3v5h5',
    title: 'Repite cuando quieras',
    description: 'Tu intento se reinicia al instante y puedes volver a intentarlo.'
  }
]

/**
 * Mockup de la pantalla de práctica de un simulador.
 *
 * Ilustra la interfaz real del producto (`/estudiante/simuladores/:id/prueba`):
 * encabezado con el cronómetro, la lista de secciones del temario y la
 * pregunta en curso con sus respuestas y la navegación entre preguntas.
 *
 * Nota editorial: es una ilustración del flujo, no del inventario. No nombra
 * ningún examen, no publica el número de reactivos ni la duración, y las
 * secciones son áreas genéricas del temario.
 */
export const simulatorMockup: LandingSimulatorMock = {
  url: 'appmisionate.com/simuladores',
  headerTitle: 'Simulador de prueba',
  headerSubtitle: 'Simulación personal, generada a partir del nivel de dificultad que elegiste.',
  timerLabel: 'Con cronómetro',
  sectionsLabel: 'Secciones',
  sections: [
    { name: 'Aritmética', status: 'done', statusLabel: 'Finalizada' },
    { name: 'Álgebra', status: 'current', statusLabel: 'En curso' },
    { name: 'Geometría', status: 'pending', statusLabel: 'Pendiente' },
    { name: 'Probabilidad', status: 'pending', statusLabel: 'Pendiente' }
  ],
  questionTitle: 'Pregunta',
  questionText:
    'Si un rectángulo mide 12 cm de largo y 5 cm de ancho, ¿cuál es su área?',
  instructionTitle: 'Instrucciones',
  instructionText: 'Selecciona una sola respuesta y continúa con la siguiente pregunta.',
  answersTitle: 'Respuestas',
  answers: [
    { label: 'A', text: '60 cm²', selected: true },
    { label: 'B', text: '34 cm²' },
    { label: 'C', text: '17 cm²' },
    { label: 'D', text: '24 cm²' }
  ],
  prevLabel: 'Anterior',
  nextLabel: 'Siguiente'
}

/**
 * Catálogo de simuladores.
 *
 * Los filtros agrupan por destino (¿a dónde quieres entrar?) y no por organismo,
 * porque el visitante no sabe qué examen presenta ni quién lo aplica.
 *
 * Nota: las etiquetas son cualitativas a propósito. El contenido del landing es
 * editorial y no se verifica contra la base de datos, así que no se publican
 * cifras de reactivos o de preguntas.
 */
export const simulatorTabs: LandingSimulatorTab[] = [
  { key: 'todos', label: 'Todos' },
  { key: 'bachillerato', label: 'Ingreso a bachillerato' },
  { key: 'universidad', label: 'Ingreso a universidad' },
  { key: 'egreso', label: 'Egreso de carrera' },
  { key: 'idiomas', label: 'Idiomas' }
]

export const simulators: LandingSimulator[] = [
  {
    id: 'ingreso-bachillerato',
    destino: 'bachillerato',
    tag: 'Bachillerato',
    tagClass: 'wl-tag-bach',
    title: 'Ingreso al bachillerato',
    glossary: 'Examen de ingreso al bachillerato con el temario vigente de cada convocatoria.',
    audience: 'Si presentas el examen de ingreso al bachillerato en tu estado.',
    chips: ['Por secciones', 'Nivel intermedio', 'Sin límite de intentos'],
    featured: true
  },
  {
    id: 'ingreso-universidad',
    destino: 'universidad',
    tag: 'Universidad',
    tagClass: 'wl-tag-universidad',
    title: 'Ingreso a la universidad',
    glossary: 'Examen nacional de ingreso a la educación superior, con el temario vigente.',
    audience: 'Si aspiras a entrar a la universidad con examen nacional.',
    chips: ['Por secciones', 'Nivel avanzado', 'Con temporizador']
  },
  {
    id: 'exacer-colbach',
    destino: 'bachillerato',
    tag: 'Bachillerato',
    tagClass: 'wl-tag-bach',
    title: 'EXACER · Colbach',
    glossary: 'Examen de selección del Colegio de Bachilleres',
    audience: 'Si presentas el EXACER del Colegio de Bachilleres.',
    chips: ['Por secciones', 'Nivel básico', 'Sin límite de intentos']
  },
  {
    id: 'egel',
    destino: 'egreso',
    tag: 'Egreso',
    tagClass: 'wl-tag-egreso',
    title: 'EGEL',
    glossary: 'Examen General para el Egreso de la Licenciatura',
    audience: 'Si estás por egresar y necesitas tu certificado de la carrera.',
    chips: ['Por secciones', 'Nivel avanzado', 'Con temporizador']
  },
  {
    id: 'toefl',
    destino: 'idiomas',
    tag: 'Idiomas',
    tagClass: 'wl-tag-idioma',
    title: 'TOEFL ITP',
    glossary: 'Test of English as a Foreign Language · versión institucional',
    audience: 'Si tu universidad pide TOEFL ITP como requisito de inglés.',
    chips: ['Por secciones', 'Nivel intermedio', 'Con temporizador']
  },
  {
    id: 'ielts',
    destino: 'idiomas',
    tag: 'Idiomas',
    tagClass: 'wl-tag-idioma',
    title: 'IELTS Academic',
    glossary: 'Certificación internacional de inglés',
    audience: 'Si tu universidad o beca exige IELTS Academic.',
    chips: ['Por secciones', 'Nivel avanzado', 'Sin límite de intentos']
  },
  {
    id: 'delf',
    destino: 'idiomas',
    tag: 'Idiomas',
    tagClass: 'wl-tag-idioma',
    title: 'DELF · Francés',
    glossary: "Diplôme d'Études en Langue Française",
    audience: 'Si estudias en Francia o te exigen el DELF.',
    chips: ['Por secciones', 'Nivel intermedio', 'Sin límite de intentos']
  },
  {
    id: 'proximos',
    tag: 'Próximamente',
    tagClass: 'wl-tag-neutral',
    title: 'Más simuladores en camino',
    glossary: 'El catálogo crece con cada nueva convocatoria y ciclo escolar.',
    audience: 'Cada simulador nuevo se arma con el temario vigente de su examen.',
    chips: [],
    placeholder: true
  }
]

/**
 * Bloque "qué es un curso".
 *
 * El copy refleja la estructura real del módulo de cursos: un curso se
 * ordena en secciones, cada sección en temas y cada tema en actividades. La
 * actividad es de tipo "Lección" o "Examen" (`CourseSubjectActivityTypes`) y
 * el avance se registra por actividad (`DoProgress`).
 */
export const courseDefinitions: LandingCourseDefinition[] = [
  {
    icon: 'list-ul',
    title: 'Un temario que te guía',
    description:
      'El curso está dividido en secciones y temas, cada uno con sus actividades. Ves el orden completo antes de empezar y sabes qué sigue después.'
  },
  {
    icon: 'books',
    title: 'Lección o examen, una actividad a la vez',
    description:
      'Cada actividad es una lección (con video y material del profesor) o un examen con preguntas. El curso te dice en cuál de las dos estás.'
  },
  {
    icon: 'bookmark',
    title: 'Un avance que no se pierde',
    description:
      'Lo que marcas como completado queda registrado. Cierras la página y retomas el curso donde lo dejaste.'
  }
]

/**
 * Bloque "qué puedes hacer dentro de un curso".
 *
 * Cada capacidad corresponde a una función que el producto ya soporta:
 * video y material descargable por actividad, exámenes con preguntas,
 * respuesta múltiple y respuesta abierta, temporizador, varios intentos y
 * avance guardado.
 */
export const courseCapabilities: LandingCourseCapability[] = [
  {
    icon: 'play',
    title: 'Lecciones en video',
    description: 'El profesor explica cada tema en video, dentro de la misma actividad.'
  },
  {
    icon: 'folder-open',
    title: 'Material descargable',
    description: 'Lecturas, guías y archivos de apoyo de cada tema, para repasar cuando quieras.'
  },
  {
    icon: 'list-ul',
    title: 'Exámenes de práctica',
    description: 'Actividades de examen con preguntas y respuestas, con el resultado al terminar.'
  },
  {
    icon: 'check-square',
    title: 'Respuesta múltiple',
    description: 'Seleccionas una o varias opciones y el sistema te dice si acertaste.'
  },
  {
    icon: 'edit',
    title: 'Respuesta abierta',
    description: 'Escribes tu respuesta y el profesor la revisa con una observación.'
  },
  {
    icon: 'clock',
    title: 'Con temporizador',
    description: 'Practicas con límite de tiempo, como en el examen real.'
  },
  {
    icon: 'redo',
    title: 'Varios intentos',
    description: 'Repites el examen las veces que quieras y comparas cómo vas.'
  },
  {
    icon: 'bookmark',
    title: 'Tu avance se guarda',
    description: 'Marcas cada actividad como completada y la barra de progreso refleja lo que llevas.'
  }
]

/**
 * Temario de ejemplo del mockup de la sección de cursos.
 *
 * Es una ilustración de cómo se ve un curso por dentro (unidades, temas y
 * actividades con su tipo), no un curso publicado: por eso no lleva cifras
 * de preguntas, horas ni estudiantes.
 */
export const courseOutline: LandingCourseUnit[] = [
  {
    title: 'Unidad 1 · Fundamentos',
    themes: [
      {
        title: 'Tema 1 · Qué vas a aprender',
        activities: [
          {
            title: 'Bienvenida y cómo usar el curso',
            kind: 'leccion',
            features: ['play', 'folder-open']
          },
          {
            title: 'Diagnóstico del tema',
            kind: 'examen',
            features: ['list-ul', 'clock']
          }
        ]
      },
      {
        title: 'Tema 2 · Conceptos base',
        activities: [
          {
            title: 'Explicación del tema en video',
            kind: 'leccion',
            features: ['play']
          },
          {
            title: 'Material de apoyo del tema',
            kind: 'leccion',
            features: ['folder-open']
          }
        ]
      }
    ]
  },
  {
    title: 'Unidad 2 · Práctica',
    themes: [
      {
        title: 'Tema 3 · Ejercicios',
        activities: [
          {
            title: 'Examen de práctica con temporizador',
            kind: 'examen',
            features: ['list-ul', 'clock']
          },
          {
            title: 'Respuesta abierta con revisión',
            kind: 'leccion',
            features: ['edit']
          }
        ]
      }
    ]
  }
]

/** Panel de detalle de una actividad en el mockup del curso. */
export const courseDetail: LandingCourseDetail = {
  tag: 'Lección',
  title: 'Explicación del tema en video',
  videoLabel: 'Video de la lección',
  items: [
    { icon: 'play', text: 'Video del profesor' },
    { icon: 'folder-open', text: 'Material descargable' },
    { icon: 'bookmark', text: 'Tu avance queda guardado' }
  ],
  progressLabel: 'Tu avance en el curso'
}

/**
 * Bloque "qué es una clase en vivo".
 *
 * El copy refleja el módulo real de clases en vivo: la clase tiene título,
 * descripción y una transmisión (`StreamUrl`), la publica un instructor
 * (`Publisher`), pertenece a un curso (`Course`) y pasa a grabación cuando
 * termina (`IsFinished`).
 */
export const liveDefinitions: LandingLiveDefinition[] = [
  {
    icon: 'play',
    title: 'Una sesión en directo',
    description:
      'El profesor entra, explica el tema y responde en el momento. La clase tiene título, descripción y una transmisión que se reproduce dentro de la plataforma.'
  },
  {
    icon: 'books',
    title: 'Cada clase cuelga de un curso',
    description:
      'La clase pertenece a un curso del catálogo: antes de entrar ya sabes de qué materia es y la encuentras junto al temario que estás estudiando.'
  },
  {
    icon: 'refresh',
    title: 'En vivo ahora o en grabación después',
    description:
      'Cuando la transmisión termina, la clase queda grabada y puedes volver a verla cuando quieras repasar el tema.'
  }
]

/**
 * Bloque "qué puedes hacer en una clase en vivo".
 *
 * Cada capacidad corresponde a una función que el producto ya soporta:
 * transmisión embebida, chat de la clase, grabación tras terminar, curso
 * asociado, instructor que publica, búsqueda por título/curso/instructor,
 * acceso desde el navegador y contenido sujeto a suscripción activa.
 */
export const liveCapabilities: LandingLiveCapability[] = [
  {
    icon: 'video',
    title: 'Transmisión dentro de la plataforma',
    description: 'El stream de la clase se reproduce en la misma pantalla donde estás, sin salir del navegador.'
  },
  {
    icon: 'comment-question',
    title: 'Chat en vivo',
    description: 'Escribes en el chat de la clase y el instructor lee lo que va llegando mientras explica.'
  },
  {
    icon: 'clock',
    title: 'Grabación para repasar',
    description: 'Termina la sesión y la clase queda disponible en grabación para verla otra vez.'
  },
  {
    icon: 'books',
    title: 'Ligada a un curso',
    description: 'Cada clase apunta a un curso, así sabes si es del tema que estás estudiando.'
  },
  {
    icon: 'award-alt',
    title: 'Con tu instructor',
    description: 'La publica un profesor de la plataforma, con su nombre y su foto, para que sepas quién te explica.'
  },
  {
    icon: 'focus-target',
    title: 'Búsqueda por título, curso o profesor',
    description: 'Encuentra la clase que buscas filtrando por cualquiera de esos tres datos.'
  },
  {
    icon: 'globe',
    title: 'Desde el navegador',
    description: 'Entras desde la computadora o el celular con la misma cuenta y la misma clase.'
  },
  {
    icon: 'check-circle',
    title: 'Con tu plan activo',
    description: 'El contenido de las clases se abre con tu suscripción activa, igual que el resto de la plataforma.'
  }
]

/**
 * Mockup de una clase en vivo.
 *
 * Ilustra la pantalla real del producto (`/estudiante/en-vivo/:id/detalles`):
 * el reproductor del stream, el instructor que publica la clase, el listado
 * de clases disponibles y el chat de la clase. Es una ilustración, no un
 * calendario real: por eso no lleva fechas ni cifras de asistentes.
 */
export const liveMock: LandingLiveMock = {
  url: 'appmisionate.com/en-vivo',
  playerBadge: 'EN VIVO',
  playerLabel: 'Clase en directo',
  instructorName: 'Profesor de Álgebra',
  instructorRole: 'Instructor de la clase',
  listLabel: 'Clases en vivo',
  listNote: 'Disponibles ahora',
  classes: [
    {
      title: 'Ecuaciones de segundo grado',
      course: 'Álgebra',
      instructor: 'Profesor de Álgebra',
      state: 'live',
      stateLabel: 'En transmisión'
    },
    {
      title: 'Funciones exponenciales',
      course: 'Funciones',
      instructor: 'Profesora de Funciones',
      state: 'live',
      stateLabel: 'En transmisión'
    },
    {
      title: 'El círculo en el plano',
      course: 'Geometría',
      instructor: 'Profesor de Geometría',
      state: 'recording',
      stateLabel: 'Grabación'
    }
  ],
  chatTitle: 'Chat en vivo',
  chatCount: '3 mensajes',
  messages: [
    { author: 'Ana', text: '¿Pueden repetir el último ejemplo?' },
    { author: 'Luis', text: 'Sí, con el método de factorización.' },
    { author: 'Karla', text: '¿Suben la guía de este tema?' }
  ],
  composerPlaceholder: 'Escribe un mensaje en el chat...'
}

export const steps: LandingStep[] = [
  {
    number: '01',
    icon: 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2|M8.5 11a4 4 0 100-8 4 4 0 000 8|M20 8v6M23 11h-6',
    title: 'Crear Tu Cuenta',
    description:
      'Regístrate gratis en 60 segundos, sin tarjetas ni complicaciones. Tu cuenta queda lista para entrar desde cualquier navegador.'
  },
  {
    number: '02',
    icon: 'M3 5h18a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V7a2 2 0 012-2z|M1 10h22',
    title: 'Suscribirse a un Plan',
    description:
      'Elige tu plan de estudio, aplica tu forma de pago y desbloquea el acceso completo a la plataforma.'
  },
  {
    number: '03',
    icon: 'M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z|M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z|M12 8.5v7|M8.5 12h7',
    title: 'Elige Curso o Crea tu Propio Simulador',
    description:
      'Inscríbete en un curso del catálogo o genera tu propia simulación, ajustando la dificultad, las materias y el tiempo.'
  },
  {
    number: '04',
    icon: 'M12 22a10 10 0 100-20 10 10 0 000 20z|M10 8l6 4-6 4z',
    title: 'Estudia, Practica y Prueba',
    description:
      'Repasa el contenido de tu curso, practica con simulacros por sección y termina tu prueba con resultados al instante.'
  }
]

export const features: LandingFeature[] = [
  {
    icon: 'database',
    title: 'Banco masivo de preguntas',
    description:
      'Más de 10,000 preguntas actualizadas, organizadas por tema, dificultad y área del conocimiento según los temarios oficiales vigentes.',
    stat: '+10,000 preguntas',
    large: true
  },
  {
    icon: 'bolt',
    title: 'Calificación instantánea',
    description:
      'Resultados al terminar cada simulacro con retroalimentación por reactivo. Sabes exactamente dónde estás.'
  },
  {
    icon: 'medal',
    title: 'Planes a tu medida',
    description:
      'Elige el plan de estudio que se adapte a tu preparación y activa tu membresía en un solo paso, sin cargos sorpresa.'
  },
  {
    icon: 'chart-bar',
    title: 'Estadísticas por área',
    description:
      'Detecta tus áreas de oportunidad: matemáticas, español, ciencias, historia. Estudia lo que más necesitas.'
  },
  {
    icon: 'book-open',
    title: 'Temario oficial vigente',
    description:
      'Contenido alineado con los planes de estudio oficiales y el formato real de cada examen.'
  },
  {
    icon: 'refresh',
    title: 'Simulacros de prueba ilimitados',
    description:
      'Practica cuantas veces necesites. El banco rotativo garantiza que siempre tengas preguntas frescas. La repetición inteligente es la clave del éxito.',
    stat: 'Ilimitado',
    large: true,
    dark: true
  }
]

export const aboutValues: LandingValue[] = [
  {
    icon: 'focus-target',
    title: 'Efectividad comprobada',
    description:
      'Simuladores 100% alineados con los temarios oficiales de cada examen.'
  },
  {
    icon: 'globe',
    title: 'Accesibilidad real',
    description:
      'Precio justo para que la preparación de calidad no sea un privilegio de pocos.'
  },
  {
    icon: 'sync',
    title: 'Actualización constante',
    description:
      'Banco de preguntas revisado con cada convocatoria y ciclo escolar.'
  }
]

/**
 * Compromisos de la plataforma que sustituyen al bloque de equipo.
 * En el hero de "Sobre nosotros" se muestran como fichas cortas (icono y
 * titulo): el copy es editorial y no publica cifras.
 */
export const aboutCommitments: LandingCommitment[] = [
  {
    icon: 'book-open',
    title: 'Temario oficial vigente'
  },
  {
    icon: 'redo',
    title: 'Simulacros ilimitados'
  },
  {
    icon: 'chart-bar',
    title: 'Calificación inmediata'
  },
  {
    icon: 'diamond',
    title: 'Estadísticas por área'
  }
]

const freeFeatures: LandingPricingFeature[] = [
  { label: '2 simuladores incluidos', included: true },
  { label: '20 preguntas por día', included: true },
  { label: 'Calificación inmediata', included: true },
  { label: 'Banco completo de preguntas', included: false },
  { label: 'Estadísticas avanzadas', included: false },
  { label: 'Sin publicidad', included: false }
]

const premiumFeatures: LandingPricingFeature[] = [
  { label: 'Todos los simuladores', included: true },
  { label: 'Preguntas ilimitadas', included: true },
  { label: 'Calificación inmediata', included: true },
  { label: 'Banco completo de preguntas', included: true },
  { label: 'Estadísticas avanzadas', included: true },
  { label: 'Sin publicidad', included: true }
]

export const plans: LandingPlan[] = [
  {
    id: 'free',
    icon: 'rocket',
    title: 'Gratuito',
    amount: '$0',
    period: 'para siempre',
    features: freeFeatures,
    cta: 'Registrarme gratis',
    ctaStyle: 'outline'
  },
  {
    id: 'premium',
    icon: 'diamond',
    title: 'Premium',
    amount: '$299',
    period: 'MXN · pago único',
    features: premiumFeatures,
    cta: 'Obtener Premium',
    ctaStyle: 'primary',
    popular: true,
    lifetimeBadge: 'Acceso de por vida incluido',
    lifetimeBadgeIcon: 'redo',
    note: 'Un solo pago. Sin renovaciones. Sin sorpresas.',
    noteIcon: 'money-bill'
  }
]

export const testimonials: LandingTestimonial[] = [
  {
    initials: 'MG',
    quote:
      'Practiqué EXANI durante tres meses y entré a la UABC. Los reactivos son casi los del examen real.',
    name: 'María G.',
    context: 'EXANI · Ingreso UABC'
  },
  {
    initials: 'CR',
    quote:
      'Con el simulador EGEL de Derecho vi qué áreas me faltaban. Aprobé en el primer intento.',
    name: 'Carlos R.',
    context: 'EGEL · Derecho'
  },
  {
    initials: 'AL',
    quote:
      'El banco del EXANI es enorme y la retroalimentación por reactivo me hizo entender mis errores.',
    name: 'Ana L.',
    context: 'EXANI · Ingreso UABC'
  },
  {
    initials: 'JP',
    quote:
      'Matemáticas era mi área débil. Practiqué con el EGEL y subí de 55% a 88% de aciertos.',
    name: 'Jorge P.',
    context: 'EGEL · Matemáticas'
  },
  {
    initials: 'VH',
    quote:
      'Los simuladores de egreso por carrera siguen el formato EGEL de CENEVAL. Practiqué desde el celular.',
    name: 'Valeria H.',
    context: 'CENEVAL · Egreso · Contabilidad'
  },
  {
    initials: 'DS',
    quote:
      'El temporizador que simula el examen real fue clave: el día del EXANI no tuve sorpresas.',
    name: 'Diego S.',
    context: 'EXANI · Ingreso UABC'
  }
]

export const faqItems: LandingFaqItem[] = [
  {
    question: '¿Qué incluye un curso?',
    answer:
      'Un curso viene con el temario completo: secciones y temas que se abren en lecciones (con video y material descargable) o en exámenes con preguntas. Además puedes practicar con temporizador, responder preguntas abiertas que revisa el profesor y retomar tu avance más tarde.'
  },
  {
    question: '¿Qué examen cubre el simulador de ingreso?',
    answer:
      'El simulador de ingreso reproduce el EXANI, el examen de selección que aplica la Universidad Autónoma de Baja California, con el temario vigente de cada convocatoria.'
  },
  {
    question: '¿Qué simuladores de egreso hay?',
    answer:
      'Los simuladores de egreso cubren los exámenes EGEL de CENEVAL, por área y por carrera, con el formato y el temario que publica el propio CENEVAL para cada guía.'
  },
  {
    question: '¿La plataforma es gratuita?',
    answer:
      'Sí, puedes crear una cuenta gratis y acceder a 2 simuladores con práctica diaria. Para acceso completo a todos los simuladores y preguntas ilimitadas, activa el plan de estudio que se adapte a tu preparación.'
  },
  {
    question: '¿Necesito instalar algo?',
    answer:
      'No. AppMisionate es una plataforma 100% web. Funciona directamente desde tu navegador en cualquier dispositivo: computadora, tablet o celular. Sin instalaciones, sin descargas.'
  },
  {
    question: '¿Cuántas preguntas tiene cada simulador?',
    answer:
      'Cada simulador tiene cientos de preguntas únicas. El banco total es de más de 10,000 reactivos organizados por tema y nivel de dificultad. Los simuladores de ingreso y de egreso cuentan con más de 2,500 preguntas cada uno.'
  },
  {
    question: '¿El contenido está actualizado al temario vigente?',
    answer:
      'Sí. El banco de preguntas se actualiza constantemente para reflejar los temarios y formatos oficiales vigentes del EXANI y de los exámenes EGEL de CENEVAL. Los usuarios Premium reciben todas las actualizaciones sin costo adicional.'
  },
  {
    question: '¿Hay garantía si no quedo satisfecho?',
    answer:
      'Sí, ofrecemos garantía de satisfacción de 7 días. Si en los primeros 7 días no estás satisfecho con el contenido, te reembolsamos el 100% de tu inversión. Sin preguntas, sin complicaciones.'
  }
]

export const contactChannels: LandingContactChannel[] = [
  {
    icon: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z|M22 6l-10 7L2 6',
    title: 'Correo electrónico',
    value: BRAND.email,
    href: `mailto:${BRAND.email}`
  },
  {
    icon: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z|M12 10m-3 0a3 3 0 106 0a3 3 0 10-6 0',
    title: 'Ubicación',
    value: BRAND.city
  },
  {
    icon: 'M12 12m-10 0a10 10 0 1020 0a10 10 0 10-20 0|M12 6v6l4 2',
    title: 'Horario de atención',
    value: BRAND.hours
  },
  {
    icon: 'M12 2a10 10 0 00-10 10c0 5 3.5 9.4 8.5 9.9a1 1 0 001.5 0c5-0.5 8.5-4.9 8.5-9.9a10 10 0 00-10-10zM8.5 14a1.5 1.5 0 110-3 1.5 1.5 0 010 3z|M12 14a1.5 1.5 0 110-3 1.5 1.5 0 010 3z|M15.5 14a1.5 1.5 0 110-3 1.5 1.5 0 010 3z',
    title: 'WhatsApp',
    value: 'Escríbenos por WhatsApp',
    href: 'https://wa.me/52',
    whatsapp: true
  }
]

export const socialLinks: LandingSocialLink[] = [
  {
    label: 'Facebook',
    href: BRAND.facebook,
    icon: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
    filled: true
  },
  {
    label: 'Instagram',
    href: BRAND.instagram,
    icon: 'M2 2h20v20H2z|M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z|M17.5 6.5h.01'
  }
]

/**
 * Enlaces del pie.
 *
 * Solo se listan destinos que existen hoy: secciones del propio landing (con su
 * ancla) y rutas reales de la aplicacion. No se enlaza a `#precios` (seccion
 * oculta) ni a entradas de un catalogo que ya no se publica.
 */
export const footerColumns: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: 'Plataforma',
    links: [
      { label: 'Cómo funciona', href: '#como-funciona' },
      { label: 'Simuladores', href: '#simuladores' },
      { label: 'Nosotros', href: '#nosotros' }
    ]
  },
  {
    title: 'Cursos',
    links: [
      { label: 'Qué incluye un curso', href: '#cursos' },
      { label: 'Clases en vivo', href: '#clases-en-vivo' }
    ]
  },
  {
    title: 'Recursos',
    links: [
      { label: 'Blog', href: '#noticias' },
      // La seccion de FAQ esta oculta: el enlace al pie se retira para no
      // apuntar a un ancla inexistente. Restituirlo al republicar `LandingFaq`.
      // { label: 'Preguntas frecuentes', href: '#faq' },
      { label: 'Contacto', href: '#contacto' },
      { label: 'Soporte', href: '/soporte' }
    ]
  },
  {
    title: 'Legal',
    links: [
      { label: 'Términos y condiciones', href: '/terminos-y-condiciones' },
      { label: 'Política de privacidad', href: '/terminos-y-condiciones' }
    ]
  }
]
